import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bold, Check, Italic, List, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface BookmarkMomentSheetProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  episodeTitle: string;
  podcastName?: string;
  artworkUrl?: string | null;
  /** Playback position (seconds) captured at the moment the user tapped Bookmark. */
  timestampSeconds: number;
}

function formatTimestamp(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/**
 * One-tap bookmark confirmation. The bookmark itself is created as soon as
 * this sheet opens (an empty-content `episode_notes` row with the captured
 * timestamp), so the user is never blocked. If they decide to add a note, the
 * same row gets updated on "Save Note"; otherwise "Done" just closes.
 */
export default function BookmarkMomentSheet({
  open,
  onClose,
  episodeId,
  episodeTitle,
  podcastName,
  artworkUrl,
  timestampSeconds,
}: BookmarkMomentSheetProps) {
  const { user } = useAuth();
  const [noteId, setNoteId] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const createdForRef = useRef<string | null>(null);

  // Create the bookmark row instantly on open.
  useEffect(() => {
    if (!open || !user) return;
    const key = `${episodeId}:${timestampSeconds}`;
    if (createdForRef.current === key) return;
    createdForRef.current = key;
    setContent("");
    setNoteId(null);

    (async () => {
      const { data, error } = await supabase
        .from("episode_notes")
        .insert({
          user_id: user.id,
          episode_id: episodeId,
          content: "",
          timestamp_seconds: Math.floor(timestampSeconds),
        })
        .select("id")
        .single();
      if (error) {
        toast.error("Could not save bookmark");
        return;
      }
      setNoteId(data.id as string);
    })();
  }, [open, user, episodeId, timestampSeconds]);

  // Reset the "created for" guard whenever the sheet closes so a fresh tap
  // at a different timestamp creates a new bookmark.
  useEffect(() => {
    if (!open) createdForRef.current = null;
  }, [open]);

  const hasContent = content.trim().length > 0;

  const handlePrimary = async () => {
    if (!hasContent) {
      onClose();
      return;
    }
    if (!noteId) {
      // Initial insert still in flight — wait briefly, then close.
      toast.message("Saving…");
      onClose();
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("episode_notes")
      .update({ content: content.trim() })
      .eq("id", noteId);
    setSaving(false);
    if (error) {
      toast.error("Bookmark saved — note will sync when online");
    } else {
      toast.success("Note saved");
    }
    onClose();
  };

  const insertFormatting = (before: string, after: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = content.substring(start, end);
    const next = content.substring(0, start) + before + selected + after + content.substring(end);
    setContent(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center px-0 sm:px-4">
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div
        className="relative w-full sm:max-w-lg bg-card border-[3px] border-foreground animate-scale-in max-h-[90vh] flex flex-col rounded-t-lg sm:rounded-md overflow-hidden"
        style={{ boxShadow: "8px 8px 0 hsl(var(--foreground))" }}
        role="dialog"
        aria-labelledby="bookmark-sheet-title"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b-[3px] border-foreground bg-muted/40 flex-shrink-0">
          <div
            className="w-10 h-10 rounded-full bg-[hsl(150,70%,45%)] text-white flex items-center justify-center border-2 border-foreground shadow-[2px_2px_0_0_hsl(var(--foreground))] flex-shrink-0 animate-scale-in"
            aria-hidden
          >
            <Check className="w-5 h-5" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="bookmark-sheet-title" className="font-mono font-bold text-base">
              Bookmark added
            </h3>
            <p className="font-mono text-[10px] text-muted-foreground truncate">
              ⏱ {formatTimestamp(timestampSeconds)}
              {podcastName ? ` · ${podcastName}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center min-h-11 min-w-11 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — episode header strip + optional note */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-center gap-3">
            {artworkUrl ? (
              <img
                src={artworkUrl}
                alt=""
                className="w-12 h-12 rounded-md border-2 border-foreground shadow-[2px_2px_0_0_hsl(var(--foreground))] flex-shrink-0"
              />
            ) : null}
            <p className="font-mono text-xs font-bold leading-snug line-clamp-2">{episodeTitle}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1 border-b border-border pb-2">
              <button
                onClick={() => insertFormatting("**", "**")}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                title="Bold"
                aria-label="Bold"
              >
                <Bold className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => insertFormatting("*", "*")}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                title="Italic"
                aria-label="Italic"
              >
                <Italic className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => insertFormatting("\n- ", "")}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                title="List"
                aria-label="List"
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>

            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              placeholder="Add a note (optional) — or just tap Done."
              className="w-full text-base sm:text-lg leading-snug px-3 py-2 bg-input border-[2px] border-foreground rounded-md focus:outline-none focus:border-accent focus:shadow-[3px_3px_0px_0px_hsl(var(--accent))] transition-[border-color,box-shadow] duration-150 resize-none"
              style={{ fontFamily: "'Architects Daughter', cursive" }}
            />
          </div>
        </div>

        {/* Footer — big tappable primary */}
        <div className="px-5 py-4 border-t-[3px] border-foreground bg-muted/30 flex-shrink-0">
          <button
            onClick={handlePrimary}
            disabled={saving}
            className="btn-comic-primary w-full disabled:opacity-50"
            style={{ minHeight: 56 }}
          >
            {saving ? "Saving…" : hasContent ? "Save Note" : "Done"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
